import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { holdsIdentity, type ShogunChopResource } from '@localagent/sdk';
import {
  DESKTOP_OFFLINE,
  readDesktopConnection,
  type DesktopConnection,
} from '@localagent/sdk/react';
import { useAtomValue } from 'jotai';
import { holdsIdentityAtom, tokenAtom } from '../stores/auth/atoms';
import { apiBaseUrlAtom } from '../stores/sdk/atoms';
import { getSDK } from '../services/sdk-instance';

interface ShogunChopContextValue {
  desktop: DesktopConnection;
  resource: ShogunChopResource | null;
}

export const ShogunChopContext = createContext<ShogunChopContextValue>({
  desktop: DESKTOP_OFFLINE,
  resource: null,
});

const POLL_INTERVAL_MS = 30_000;

function sameDesktop(a: DesktopConnection, b: DesktopConnection): boolean {
  if (a.state !== b.state || a.desktopDevices.length !== b.desktopDevices.length) return false;
  return a.desktopDevices.every(
    (d, i) =>
      d.device_id === b.desktopDevices[i].device_id &&
      d.connected_at === b.desktopDevices[i].connected_at &&
      d.last_heartbeat === b.desktopDevices[i].last_heartbeat,
  );
}

export function ShogunChopProvider({
  resource,
  children,
}: {
  resource: ShogunChopResource | null;
  children: ReactNode;
}) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const token = useAtomValue(tokenAtom);
  const apiBaseUrl = useAtomValue(apiBaseUrlAtom);
  const [desktop, setDesktop] = useState<DesktopConnection>(DESKTOP_OFFLINE);

  useEffect(() => {
    if (!isAuthenticated || !apiBaseUrl || !token) {
      setDesktop(DESKTOP_OFFLINE);
      return;
    }

    let cancelled = false;
    const tick = () => {
      readDesktopConnection(getSDK().bridge)
        .then((next) => {
          if (!cancelled) setDesktop((prev) => (sameDesktop(prev, next) ? prev : next));
        })
        .catch((err: unknown) => {
          // no-fallbacks.md: a poll still in flight when the credential is released is not a device failure; releasing resets to offline.
          if (cancelled || !holdsIdentity(getSDK().identity.phase)) return;
          console.error('[ShogunChopProvider] device poll failed', err);
          setDesktop(DESKTOP_OFFLINE);
        });
    };

    tick();
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated, apiBaseUrl, token]);

  const value = useMemo<ShogunChopContextValue>(
    () => ({ desktop, resource }),
    [desktop, resource],
  );

  return <ShogunChopContext.Provider value={value}>{children}</ShogunChopContext.Provider>;
}

export function useShogunchop() {
  return useContext(ShogunChopContext);
}
